import { Routes } from '../../enums/Routes';
import { getHealthCategoryKey } from '../../utils/healthCategory';
import { buildRoute } from '../../utils/routeBuilder';
import type {
  Activity,
  ActivityType,
  Channel,
  Chosen,
  NowCard,
  ProcessStep,
  StepState,
  SummaryState,
  SurveyProcess,
} from '../../api/surveyProcess';

/** Text to translate: an i18n key and what goes into it */
export type Text = { key: string; values?: Record<string, string | number> };

export type Fact = { id: string; label: string; value: Text; progress?: number };

export type ActivityEntry = {
  id: string;
  type: ActivityType;
  at: string;
  text: Text;
  date: string;
  channels: Channel[];
  failedChannels: Channel[];
};

/** The latest four, as many as fit under the card before the team leader asks for more */
const ACTIVITY_SHOWN = 4;

/** The team leader can send a new reminder a day after the last */

const PREFIX = 'surveyProcess';

const locale = (language: string) => (language === 'en' ? 'en-GB' : 'nb-NO');

/** «2. september», in Norwegian time, as the team leader reads it */
export const dayAndMonth = (value: string, language: string) =>
  new Intl.DateTimeFormat(locale(language), { day: 'numeric', month: 'long', timeZone: 'Europe/Oslo' }).format(
    new Date(value),
  );

/** «15. januar 2027», for a date further ahead */
const dayMonthAndYear = (value: string, language: string) =>
  new Intl.DateTimeFormat(locale(language), {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'Europe/Oslo',
  }).format(new Date(value));

/** «08:00», in Norwegian time */
const clock = (value: Date, language: string) =>
  new Intl.DateTimeFormat(locale(language), { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Oslo' }).format(
    value,
  );

const weekdayDayMonth = (value: Date, language: string) =>
  new Intl.DateTimeFormat(locale(language), {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: 'Europe/Oslo',
  }).format(value);

/** «torsdag 10. september kl. 08:00», for what goes out at a set time */
const dayAndTime = (value: string, language: string) =>
  text('time.at', { day: weekdayDayMonth(new Date(value), language), time: clock(new Date(value), language) });

/** «torsdag 29. oktober rundt kl. 20», for what Trustpulse expects to send, which is never to the minute */
const dayAndHour = (value: string, language: string) => {
  const rounded = new Date(Math.round(new Date(value).getTime() / (60 * 60 * 1000)) * 60 * 60 * 1000);
  return text('time.around', {
    day: weekdayDayMonth(rounded, language),
    hour: clock(rounded, language).slice(0, 2),
  });
};

const percent = (part: number, whole: number) => (whole > 0 ? Math.min(100, Math.round((part / whole) * 100)) : 0);

const text = (key: string, values?: Text['values']): Text => ({ key: `${PREFIX}.${key}`, values });

/** A time is itself text to translate, so a text that holds one is spelled out in two steps */
export type TimedText = Text & { time?: Text };

const timed = (key: string, time: Text): TimedText => ({ ...text(key), time });

const nextSurveyText = (nextSurveyAt: string | null, language: string): Text =>
  nextSurveyAt ? text('facts.date', { date: dayAndMonth(nextSurveyAt, language) }) : text('facts.notPlanned');

/** What the team leader sees of the reminders: the next one, the one on its way, the last one, or what will come */
const reminderText = (now: Extract<NowCard, { step: 'SURVEY' }>, language: string): TimedText => {
  if (now.nextReminderAt) return timed('sending.sends', dayAndTime(now.nextReminderAt, language));
  if (now.reminderBlockedBy === 'comingSoon') return text('reminder.onItsWay');
  if (now.lastReminderAt) return timed('sending.sentAt', dayAndTime(now.lastReminderAt, language));
  return text(now.remindsBeforeDeadline ? 'facts.remindsAutomatically' : 'facts.noReminder');
};

const SUMMARY_TITLE: Partial<Record<SummaryState['state'], string>> = {
  expected: 'now.title.summaryComing',
  preparing: 'now.title.summaryComing',
  queued: 'now.title.summaryComing',
  notSent: 'now.title.summaryNotSent',
};

/** The summary is being written now, and goes out or into the queue within minutes */
export const isPreparingSummary = (process: SurveyProcess | null | undefined): boolean =>
  process?.now.step === 'ACTION_PLAN' && process.now.summary.state === 'preparing';

const isFirstRound = (process: SurveyProcess) => process.round === 1;

/** «Runde 3 · startet 2. september». The first survey is named as such, since there is nothing to compare it with */
export const roundHeading = (process: SurveyProcess, language: string): Text => {
  const date = dayAndMonth(process.survey.startedAt, language);
  return isFirstRound(process) ? text('round.first', { date }) : text('round.number', { round: process.round, date });
};

/** «Runde 3 er fullført», once the summary has gone to the team */
export const completedTitle = (process: SurveyProcess): Text | null => {
  if (!process.completed) return null;
  return isFirstRound(process) ? text('completed.titleFirst') : text('completed.title', { round: process.round });
};

/** The card tells what happens now, or what the round came to once it is over */
export const nowLabel = (process: SurveyProcess): Text => text(process.completed ? 'now.completedLabel' : 'now.label');

export const nowTitle = (process: SurveyProcess): Text => {
  const { now } = process;
  if (now.step === 'ACTION_PLAN') return text(SUMMARY_TITLE[now.summary.state] ?? 'now.title.ACTION_PLAN');
  if (now.step !== 'RESULTS') return text(`now.title.${now.step}`);
  if (now.tooFewResponses) return text('now.title.tooFewResponses');
  if (now.analysisFailed) return text('now.title.resultsDelayed');
  return text(now.resultsReady ? 'now.title.resultsReady' : 'now.title.resultsMaking');
};

const allContributed = (process: SurveyProcess) => process.totals.contributors >= process.totals.teamSize;

/** What the card says right under its title, when the step needs explaining */
export const nowLead = (process: SurveyProcess): Text | null => {
  const { now } = process;
  if (now.step === 'RESULTS' && now.tooFewResponses)
    return now.requiredResponses
      ? text('now.lead.tooFewResponsesNeeded', { count: now.requiredResponses })
      : text('now.lead.tooFewResponses');
  if (now.step === 'RESULTS' && now.analysisFailed) return text('now.lead.analysisFailed');
  if (now.step === 'SURVEY' && now.deadlineExtended)
    return now.requiredResponses
      ? text('now.lead.deadlineExtendedNeeded', { count: now.requiredResponses })
      : text('now.lead.deadlineExtended');
  if (now.step === 'ACTION_PLAN' && now.summary.state === 'notSent')
    return text(allContributed(process) ? 'now.lead.summaryNotSentAll' : 'now.lead.summaryNotSent');
  if (now.step === 'SURVEY' && isFirstRound(process)) return text('now.lead.firstSurvey');
  return null;
};

const summaryText = (now: Extract<NowCard, { step: 'ACTION_PLAN' }>, language: string): TimedText => {
  const { summary } = now;
  switch (summary.state) {
    case 'waitingForContributors':
      return summary.waitsUntil
        ? text('facts.summaryWhenUntil', { count: now.contributorsNeeded, date: dayAndMonth(summary.waitsUntil, language) })
        : text('facts.summaryWhen', { count: now.contributorsNeeded });
    case 'expected':
      return timed('sending.sends', dayAndHour(summary.at, language));
    case 'queued':
      return summary.at ? timed('sending.sends', dayAndTime(summary.at, language)) : text('sending.soon');
    case 'preparing':
    case 'notSent':
      return text(`summary.${summary.state}`);
  }
};

const invitationText = (now: Extract<NowCard, { step: 'ACTION_PLAN' }>, language: string): TimedText => {
  if (now.invitationSentAt) return text('sending.sent', { date: dayAndMonth(now.invitationSentAt, language) });
  if (now.invitationSendsAt) return timed('sending.sends', dayAndTime(now.invitationSendsAt, language));
  if (now.invitationExpectedAt) return timed('sending.sends', dayAndHour(now.invitationExpectedAt, language));
  if (now.invitationNotReached) return text('sending.notReached');
  return text('sending.soon');
};

/** The reminder of the action plan is shown once there is one to show */
const actionPlanReminderFact = (now: Extract<NowCard, { step: 'ACTION_PLAN' }>, language: string): Fact[] => {
  if (now.reminderBlockedBy === 'comingSoon')
    return [{ id: 'reminder', label: `${PREFIX}.facts.reminder`, value: text('reminder.onItsWay') }];
  if (now.lastReminderAt)
    return [
      { id: 'reminder', label: `${PREFIX}.facts.reminder`, value: timed('sending.sentAt', dayAndTime(now.lastReminderAt, language)) },
    ];
  return [];
};

export const nowFacts = (now: NowCard, language: string): Fact[] => {
  switch (now.step) {
    case 'SURVEY':
      return [
        {
          id: 'answers',
          label: `${PREFIX}.facts.answers`,
          value: text('facts.count', { count: now.responses, total: now.expected }),
          progress: percent(now.responses, now.expected),
        },
        { id: 'closes', label: `${PREFIX}.facts.closes`, value: text('facts.date', { date: dayAndMonth(now.closesAt, language) }) },
        { id: 'reminder', label: `${PREFIX}.facts.reminder`, value: reminderText(now, language) },
      ];
    case 'RESULTS':
      return now.tooFewResponses
        ? [
            { id: 'answers', label: `${PREFIX}.facts.answered`, value: text('facts.count', { count: now.responses, total: now.expected }) },
            { id: 'closed', label: `${PREFIX}.facts.closed`, value: text('facts.date', { date: dayAndMonth(now.closedAt, language) }) },
            { id: 'nextSurvey', label: `${PREFIX}.facts.nextSurvey`, value: nextSurveyText(now.nextSurveyAt, language) },
          ]
        : [
            { id: 'answers', label: `${PREFIX}.facts.answered`, value: text('facts.count', { count: now.responses, total: now.expected }) },
            {
              id: 'shared',
              label: `${PREFIX}.facts.toTeam`,
              value: now.resultSharedAt
                ? text('sending.sent', { date: dayAndMonth(now.resultSharedAt, language) })
                : now.resultSharesAt
                  ? timed('sending.sends', dayAndTime(now.resultSharesAt, language))
                  : text(now.resultsReady ? 'sending.soon' : 'sending.whenReady'),
            },
          ];
    case 'ACTION_PLAN':
      return [
        {
          id: 'contributors',
          label: `${PREFIX}.facts.contributors`,
          value: text('facts.count', { count: now.contributors, total: now.teamSize }),
          progress: percent(now.contributors, now.teamSize),
        },
        { id: 'invitation', label: `${PREFIX}.facts.invitation`, value: invitationText(now, language) },
        { id: 'summary', label: `${PREFIX}.facts.summary`, value: summaryText(now, language) },
        ...actionPlanReminderFact(now, language),
      ];
    case 'ACTION_PLAN_RESULT':
      return [
        {
          id: 'contributors',
          label: `${PREFIX}.facts.contributors`,
          value: text('facts.count', { count: now.contributors, total: now.teamSize }),
          progress: percent(now.contributors, now.teamSize),
        },
        {
          id: 'summary',
          label: `${PREFIX}.facts.summary`,
          value: now.summarySentAt
            ? text('sending.sentToTeam', { date: dayAndMonth(now.summarySentAt, language) })
            : text('sending.sentToTeamNoDate'),
        },
      ];
  }
};

/** What each step came to, as a short line under it. A step not started yet has none */
/** Surveys from before Trustpulse logged what it sent have no time, so the line says it went out without one */
const sentLine = (key: string, at: string | null, language: string): Text =>
  at ? text(key, { date: dayAndMonth(at, language) }) : text(`${key}NoDate`);

export const stepLine = (process: SurveyProcess, step: ProcessStep, state: StepState, language: string): Text | null => {
  const { totals } = process;
  const count = (key: string, done: number, of: number) => text(key, { count: done, total: of });
  if (state === 'stopped') return text('stepLine.stopped');
  if (state === 'notStarted') return null;
  switch (step) {
    case 'SURVEY':
      return count(state === 'done' ? 'stepLine.answered' : 'stepLine.answering', totals.responses, totals.expected);
    case 'RESULTS':
      return state === 'done' ? sentLine('stepLine.shared', totals.resultSharedAt, language) : null;
    case 'ACTION_PLAN':
      return count(state === 'done' ? 'stepLine.contributed' : 'stepLine.contributing', totals.contributors, totals.teamSize);
    case 'ACTION_PLAN_RESULT':
      return text('stepLine.summarySent');
  }
};

/** The API names the focus area in the language of the team, so it is translated to the language of the app */
export const focusAreaOf = (process: SurveyProcess, translate: (key: string) => string): string | null => {
  if (!process.actionPlan) return null;
  const key = getHealthCategoryKey(process.actionPlan.focusArea);
  return key ? translate(`teamHealthCategories.${key}.title`) : process.actionPlan.focusArea;
};

/** The focus area of the action plan, under its step once the team works on it */
export const stepFocus = (
  process: SurveyProcess,
  step: ProcessStep,
  state: StepState,
  translate: (key: string) => string,
): Text | null => {
  const focus = focusAreaOf(process, translate);
  return step === 'ACTION_PLAN' && state !== 'notStarted' && focus ? text('stepLine.focus', { focus }) : null;
};

/** The page the team is asked to open in this step. A survey with too few answers has none, and the action plan has its own card */
export const linkOf = (process: SurveyProcess, origin: string): { label: string; url: string } | null => {
  const guid = process.survey.uuid;
  const link = (label: string, route: Routes) => ({ label: `${PREFIX}.links.${label}`, url: `${origin}${buildRoute(route, { guid })}` });
  switch (process.step) {
    case 'SURVEY':
      return link('survey', Routes.FormSubmission);
    case 'RESULTS':
      return process.tooFewResponses ? null : link('results', Routes.PublicTeamHealthMetrics);
    case 'ACTION_PLAN':
      return link('actionPlan', Routes.PublicActionPlanDirect);
    case 'ACTION_PLAN_RESULT':
      return null;
  }
};

/** The report the team leader opens from the card once there are results. The action plan opens from its own card */
export const pageOf = (process: SurveyProcess, teamId: number): { label: string; to: string } | null => {
  if (process.step === 'ACTION_PLAN_RESULT') return null;
  const ready = process.step !== 'SURVEY' && !process.tooFewResponses && !(process.now.step === 'RESULTS' && !process.now.resultsReady);
  return ready
    ? { label: `${PREFIX}.links.report`, to: buildRoute(Routes.InterpretiveReport, { teamId, surveyId: process.survey.id }) }
    : null;
};

/** The team leader plans the next survey here when too few answered and none is planned after this one */
export const asksToPlanNextSurvey = (now: NowCard) =>
  now.step === 'RESULTS' && now.tooFewResponses && now.nextSurveyAt === null;

/**
 * Once the team has the action plan, the next survey starts the next round and shows whether the plan worked. It says
 * when that is, or asks to plan it
 */
export const nextSurveyOf = (process: SurveyProcess, language: string): { text: Text; planned: boolean } | null => {
  const { now } = process;
  if (now.step !== 'ACTION_PLAN_RESULT') return null;
  return now.nextSurveyAt
    ? {
        text: text('nextSurvey.planned', { round: process.round + 1, date: dayMonthAndYear(now.nextSurveyAt, language) }),
        planned: true,
      }
    : { text: text('nextSurvey.none'), planned: false };
};

const BY_TEAM_LEADER: Partial<Record<ActivityType, string>> = {
  REMINDER_SENT: 'REMINDER_SENT_BY_LEADER',
  ACTION_PLAN_REMINDER_SENT: 'ACTION_PLAN_REMINDER_SENT_BY_LEADER',
};

/** A message no channel took never reached the team, so the entry does not say it was sent */
const reachedNoChannel = (activity: Activity) => activity.channels.length === 0 && activity.failedChannels.length > 0;

const activityText = (activity: Activity, language: string): Text => {
  if (reachedNoChannel(activity)) return text(`activity.notReached.${activity.type}`);
  const key = `activity.${(activity.byTeamLeader && BY_TEAM_LEADER[activity.type]) || activity.type}`;
  switch (activity.type) {
    case 'REMINDER_SENT':
    case 'TOO_FEW_RESPONSES':
    case 'RESULTS_READY':
      return text(key, { count: activity.responses ?? 0, total: activity.expected ?? 0 });
    case 'DEADLINE_EXTENDED':
      return text(key, { date: activity.deadline ? dayAndMonth(activity.deadline, language) : '' });
    default:
      return text(key);
  }
};

/** The latest first. All of them when the team leader asks for them */
export const activityEntries = (activity: Activity[], language: string, all = false): ActivityEntry[] =>
  (all ? activity : activity.slice(0, ACTIVITY_SHOWN)).map((entry) => ({
    id: `${entry.type}-${entry.at}`,
    type: entry.type,
    at: entry.at,
    text: activityText(entry, language),
    date: `${dayAndMonth(entry.at, language)} ${clock(new Date(entry.at), language)}`,
    channels: entry.channels,
    failedChannels: entry.failedChannels,
  }));

export const hasMoreActivity = (activity: Activity[]) => activity.length > ACTIVITY_SHOWN;

/** «Teams (Generelt), Slack og e-post» */
export const channelList = (channels: Channel[], nameOf: (channel: Channel) => string, and: string) => {
  const names = channels.map(nameOf);
  return names.length < 2 ? names.join('') : `${names.slice(0, -1).join(', ')} ${and} ${names[names.length - 1]}`;
};

/** The action plan as the team sees it once the summary has gone out: its link, and what the team chose most */
export type PlanCard = { url: string; chosen: Text[] };

/** «Teamet gjør: be om hjelp, og 2 til». The API gives the statements with the most chosen first */
const mostChosen = (key: 'teamDoes' | 'teamNeeds', chosen: Chosen[]): Text[] => {
  const [first, ...rest] = chosen;
  if (!first) return [];
  return [text(`planCard.${key}${rest.length > 0 ? 'More' : ''}`, { statement: first.statement, count: rest.length })];
};

export const planCardOf = (process: SurveyProcess, origin: string): PlanCard | null => {
  if (process.step !== 'ACTION_PLAN_RESULT') return null;
  const said = process.actionPlan?.said;
  return {
    url: `${origin}${buildRoute(Routes.PublicActionPlanDirect, { guid: process.survey.uuid })}`,
    chosen: said ? [...mostChosen('teamDoes', said.commitments), ...mostChosen('teamNeeds', said.expectations)] : [],
  };
};

/** The team leader can remind the team once a day, while it answers or makes the action plan */
export type ReminderAction = { canSend: true; label: string } | { canSend: false; text: TimedText };

export const reminderAction = (now: NowCard, language: string): ReminderAction | null => {
  if (now.step !== 'SURVEY' && now.step !== 'ACTION_PLAN') return null;
  if (now.reminderBlockedBy === 'closed' || now.reminderBlockedBy === 'comingSoon') return null;
  if (!now.reminderBlockedBy)
    return { canSend: true, label: `${PREFIX}.reminder.${now.step === 'SURVEY' ? 'send' : 'sendActionPlan'}` };
  return now.remindAgainAt
    ? { canSend: false, text: timed('reminder.again', dayAndTime(now.remindAgainAt, language)) }
    : null;
};
